import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, Award, BookOpen, GraduationCap, Presentation } from 'lucide-react';

const Events = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const events = [
    // Conferences
    {
      id: 1,
      type: 'conference',
      title: 'International Conference on Advanced Research in Science & Technology 2025',
      date: 'March 15-17, 2025',
      time: '9:00 AM - 6:00 PM',
      venue: 'Grand Convention Center, Mumbai',
      description: 'Join leading researchers and academicians to discuss cutting-edge developments in science and technology.',
      registrations: 450,
      status: 'upcoming',
      image: '/events/conference1.jpg'
    },
    {
      id: 2,
      type: 'conference',
      title: 'Global Education Innovation Summit 2025',
      date: 'June 20-22, 2025',
      time: '10:00 AM - 5:00 PM',
      venue: 'India International Centre, Delhi',
      description: 'Exploring innovative approaches to education and learning in the digital age.',
      registrations: 320,
      status: 'upcoming',
      image: '/events/conference2.jpg'
    },
    // Awards
    {
      id: 3,
      type: 'award',
      title: 'Excellence in Education Awards 2025',
      date: 'April 10, 2025',
      time: '6:00 PM - 9:00 PM',
      venue: 'Hotel Taj Palace, Chennai',
      description: 'Recognizing outstanding contributions to education and research excellence.',
      registrations: 200,
      status: 'upcoming',
      image: '/events/award1.jpg'
    },
    {
      id: 4,
      type: 'award',
      title: 'Innovation in Technology Awards 2025',
      date: 'August 15, 2025',
      time: '7:00 PM - 10:00 PM',
      venue: 'Bangalore International Exhibition Centre',
      description: 'Celebrating breakthrough innovations in educational technology and research.',
      registrations: 150,
      status: 'upcoming',
      image: '/events/award2.jpg'
    },
    // Workshops
    {
      id: 5,
      type: 'workshop',
      title: 'AI in Education: Practical Implementation Workshop',
      date: 'February 28, 2025',
      time: '9:00 AM - 4:00 PM',
      venue: 'IIT Madras, Chennai',
      description: 'Hands-on workshop on implementing AI tools and techniques in educational settings.',
      registrations: 80,
      status: 'upcoming',
      image: '/events/workshop1.jpg'
    },
    {
      id: 6,
      type: 'workshop',
      title: 'Research Methodology and Publication Workshop',
      date: 'May 12, 2025',
      time: '10:00 AM - 5:00 PM',
      venue: 'Anna University, Chennai',
      description: 'Learn effective research methodologies and publication strategies for academic success.',
      registrations: 120,
      status: 'upcoming',
      image: '/events/workshop2.jpg'
    },
    // FDP (Faculty Development Programs)
    {
      id: 7,
      type: 'fdp',
      title: 'Faculty Development Program on Digital Pedagogy',
      date: 'March 5-9, 2025',
      time: '9:00 AM - 5:00 PM',
      venue: 'VIT University, Vellore',
      description: 'Five-day intensive program to enhance faculty skills in digital teaching methodologies.',
      registrations: 60,
      status: 'upcoming',
      image: '/events/fdp1.jpg'
    },
    {
      id: 8,
      type: 'fdp',
      title: 'Advanced Research Techniques FDP',
      date: 'July 14-18, 2025',
      time: '9:30 AM - 4:30 PM',
      venue: 'SRM Institute of Science and Technology',
      description: 'Comprehensive program covering advanced research methodologies and data analysis techniques.',
      registrations: 45,
      status: 'upcoming',
      image: '/events/fdp2.jpg'
    }
  ];

  const categories = [
    { key: 'all', label: 'All Events', icon: '📅', color: '#667eea' },
    { key: 'conference', label: 'Conferences', icon: '🎤', color: '#48bb78' },
    { key: 'award', label: 'Awards', icon: '🏆', color: '#ffd700' },
    { key: 'workshop', label: 'Workshops', icon: '🛠️', color: '#ed8936' },
    { key: 'fdp', label: 'FDP', icon: '🎓', color: '#9f7aea' }
  ];

  const getFilteredEvents = () => {
    if (activeCategory === 'all') return events;
    return events.filter(event => event.type === activeCategory);
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'conference': return <Presentation size={24} />;
      case 'award': return <Award size={24} />;
      case 'workshop': return <BookOpen size={24} />;
      case 'fdp': return <GraduationCap size={24} />;
      default: return <Calendar size={24} />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'conference': return '#48bb78';
      case 'award': return '#ffd700';
      case 'workshop': return '#ed8936';
      case 'fdp': return '#9f7aea';
      default: return '#667eea';
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section className="hero-section text-center" style={{ 
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9), rgba(118, 75, 162, 0.9)), url("https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container-custom">
          <h1 className="heading-1 text-white mb-6">Upcoming Events</h1>
          <p className="body-large text-white mb-8" style={{ maxWidth: '600px', margin: '0 auto 2rem', opacity: 0.95 }}>
            Join our conferences, workshops, awards ceremonies, and faculty development programs
          </p>
        </div>
      </section>

      <div className="container-custom" style={{ padding: '80px 24px' }}>
        {/* Category Filter */}
        <div style={{ marginBottom: '60px' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '40px', color: '#2d3748', fontSize: '2.5rem', fontWeight: 700 }}>
            Event Categories
          </h2>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px',
            marginBottom: '40px'
          }}>
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => setActiveCategory(category.key)}
                style={{
                  padding: '20px',
                  backgroundColor: activeCategory === category.key ? category.color : 'white',
                  color: activeCategory === category.key ? 'white' : category.color,
                  border: `2px solid ${category.color}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  if (activeCategory !== category.key) {
                    e.target.style.backgroundColor = category.color;
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeCategory !== category.key) {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = category.color;
                  }
                }}
              >
                <span style={{ fontSize: '2rem' }}>{category.icon}</span>
                {category.label}
                <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                  {category.key === 'all' ? events.length : events.filter(e => e.type === category.key).length} Events
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Events Grid */}
        <div style={{ marginBottom: '60px' }}>
          <h3 style={{ 
            color: '#2d3748', 
            fontSize: '2rem', 
            fontWeight: 700, 
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            {activeCategory === 'all' ? 'All Events' : categories.find(c => c.key === activeCategory)?.label}
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
            gap: '30px'
          }}>
            {getFilteredEvents().map((event) => (
              <div key={event.id} style={{ 
                background: `linear-gradient(135deg, ${getEventColor(event.type)}20 0%, ${getEventColor(event.type)}10 100%)`, 
                padding: '3px', 
                borderRadius: '20px', 
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <div style={{ 
                  backgroundColor: 'white', 
                  padding: '30px', 
                  borderRadius: '17px'
                }}>
                  {/* Event Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: getEventColor(event.type),
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      {getEventIcon(event.type)}
                    </div>
                    <div>
                      <span style={{
                        backgroundColor: getEventColor(event.type),
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {event.type}
                      </span>
                    </div>
                  </div>

                  {/* Event Details */}
                  <h4 style={{ 
                    color: '#2d3748', 
                    fontSize: '1.3rem', 
                    fontWeight: 700, 
                    marginBottom: '15px',
                    lineHeight: '1.3'
                  }}>
                    {event.title}
                  </h4>

                  <p style={{ 
                    color: '#4a5568', 
                    fontSize: '1rem', 
                    lineHeight: '1.6', 
                    marginBottom: '20px' 
                  }}>
                    {event.description}
                  </p>

                  {/* Event Info */}
                  <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Calendar size={16} color={getEventColor(event.type)} />
                      <span style={{ color: '#4a5568', fontSize: '0.9rem', fontWeight: 600 }}>{event.date}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Clock size={16} color={getEventColor(event.type)} />
                      <span style={{ color: '#4a5568', fontSize: '0.9rem', fontWeight: 600 }}>{event.time}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <MapPin size={16} color={getEventColor(event.type)} />
                      <span style={{ color: '#4a5568', fontSize: '0.9rem', fontWeight: 600 }}>{event.venue}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Users size={16} color={getEventColor(event.type)} />
                      <span style={{ color: '#4a5568', fontSize: '0.9rem', fontWeight: 600 }}>{event.registrations} Registered</span>
                    </div>
                  </div>

                  {/* Register Button */}
                  <button 
                    onClick={() => navigate(`/event-registration/${event.id}`)}
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      backgroundColor: getEventColor(event.type),
                      color: 'white',
                      border: 'none',
                      borderRadius: '25px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    Register Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          padding: '3px', 
          borderRadius: '20px', 
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)' 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '60px', 
            borderRadius: '17px',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '20px' }}>
              Don't Miss Out!
            </h2>
            <p style={{ color: '#718096', fontSize: '1.2rem', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px' }}>
              Stay updated with our latest events and opportunities. Subscribe to our newsletter for early access to registrations.
            </p>
            <button style={{
              padding: '18px 40px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '1.2rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
              Subscribe for Updates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;